import React, { useMemo, useState } from 'react';
import { Button, Popover, Stack, rem, Select, Flex, Text, Checkbox } from '@mantine/core';
import { IoIosArrowDown } from 'react-icons/io';
import { PiMagicWand } from 'react-icons/pi';
import { Mention, MentionsInput } from 'react-mentions';

interface MentionPopoverProps {
  text: string;
  onChange: (value: string) => void;
  mentionItems: string[];
  onAdd: (displayName: string, newValue: string) => void;
  isInitialyOpen: boolean;
  onClosePopover: () => void;
  selectedSuggestions?: string[];
  browserUse?: boolean;
  onBrowserUseChange?: (checked: boolean) => void;
}

export function MentionPopover({
  text,
  onChange,
  mentionItems,
  onAdd,
  isInitialyOpen,
  onClosePopover,
  selectedSuggestions,
  browserUse = false,
  onBrowserUseChange
}: MentionPopoverProps) {
  const [opened, setOpened] = useState<boolean>(isInitialyOpen);
  const [value, setValue] = useState<string>(text);
  const [hasError, setHasError] = useState<boolean>(false);

  const filteredSuggestions = useMemo(() => {
    return selectedSuggestions?.filter(sug => value.includes(sug));
  }, [selectedSuggestions, value]);

  const doesContainMention = useMemo(() => {
    const matches = [...value.matchAll(/@\[(.+?)\]/g)].map(m => m[1]);

    return mentionItems.some(item => matches.includes(item));
  }, [value, mentionItems]);

  const isValidToSubmit =
    // @ts-ignore
    filteredSuggestions?.length > 0 && filteredSuggestions?.length < 6;

  const handleSubmit = () => {
    if (!isValidToSubmit && !doesContainMention) return setHasError(true);

    onChange(value);
    setOpened(false);
    onClosePopover();
    setHasError(false);
  };

  const handleChangeValue = (val: string) => {
    setValue(val);
  };

  return (
    <Popover
      opened={isInitialyOpen || opened}
      onChange={val =>
        // @ts-ignore
        setOpened(() => {
          if (!val) return onClosePopover();
          return val;
        })
      }
      width={400}
      position="bottom"
    >
      <Popover.Target>
        <Select
          value={text}
          placeholder={text}
          onClick={e => {
            e.preventDefault();
            e.stopPropagation();
            setOpened(o => !o);
          }}
          h={rem(30)}
          mah={rem(30)}
          w="100%"
          size="xs"
          rightSection={<IoIosArrowDown />}
          styles={{
            input: {
              backgroundColor: 'var(--input-disabled-bg)',
              color: 'var(--input-disabled-color)'
            }
          }}
        />
      </Popover.Target>

      <Popover.Dropdown
        p={rem(8)}
        style={{
          boxShadow: '0px 1px 2px 0px rgba(0, 0, 0, 0.10), 0px 1px 3px 0px rgba(0, 0, 0, 0.05)'
        }}
      >
        <Stack gap="xs" style={{ position: 'relative' }}>
          <MentionsInput
            placeholder="Enter your prompt"
            value={value}
            onChange={e => handleChangeValue(e.target.value)}
            className="mention-input"
            style={{
              input: {
                overflow: 'auto',
                height: 72,
                borderColor: hasError ? 'red' : '#228be6'
              },
              highlighter: {
                boxSizing: 'border-box',
                overflow: 'hidden',
                height: 72
              },
              suggestions: {
                maxHeight: 250,
                overflowY: 'auto'
              }
            }}
          >
            <Mention
              trigger="@"
              data={mentionItems?.map((item, idx) => ({ id: idx, display: item }))}
              style={{ backgroundColor: '#cae9ff', borderRadius: '4px', color: '#fff' }}
              displayTransform={(id, display) => `@${display}`}
              markup="@[__display__]"
              appendSpaceOnAdd={true}
              onAdd={(_, displayName) => onAdd(displayName, value + displayName)}
              renderSuggestion={(suggestion, search, highlightedDisplay, index, focused) => (
                <Button
                  w="100%"
                  ta="left"
                  display="flex"
                  variant={focused ? 'outline' : 'transparent'}
                  c={!focused ? '#000' : 'blue'}
                  justify="flex-start"
                >
                  {highlightedDisplay}
                </Button>
              )}
            />
          </MentionsInput>

          {hasError && (
            <Text fz={rem(12)} c="red.6">
              Use @ to select at least 1 and up to 5 columns in the textarea.
            </Text>
          )}

          <Flex w="100%" justify="space-between" align="center" mt="xs">
            <Flex align="center" gap="xs">
              {onBrowserUseChange && (
                <Checkbox
                  label="Web search"
                  size="xs"
                  checked={browserUse}
                  onChange={e => onBrowserUseChange(e.currentTarget.checked)}
                />
              )}
            </Flex>
            <Flex>
              <Button size="xs" onClick={handleSubmit} rightSection={<PiMagicWand size={12} />}>
                Submit
              </Button>
            </Flex>
          </Flex>
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
}
